import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { ExternalLink, Play } from "lucide-react";
import portfolioSample1 from "/public/portfolio-sample-1.jpg";
import portfolioSample2 from "/public/portfolio-sample-2.jpg";
import portfolioSample3 from "/public/portfolio-sample-3.jpg";

const FoundersPortfolioSection = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const portfolioItems = [
    {
      id: 1,
      title: "Echoes of Tomorrow",
      category: "music-video",
      type: "Music Video",
      client: "Stellar Records",
      description: "A visually stunning music video exploring themes of time and memory through innovative cinematography and lighting design.",
      image: portfolioSample1,
      tags: ["Music Video", "Color Grading", "VFX"],
      credits: "Director: Sarah Mitchell | Artist: Luna Moon"
    },
    {
      id: 2,
      title: "Behind the Lens",
      category: "documentary",
      type: "Documentary",
      client: "Independent",
      description: "An intimate documentary following the creative process of three emerging filmmakers in their pursuit of artistic authenticity.",
      image: portfolioSample2,
      tags: ["Documentary", "Interview", "Natural Lighting"],
      credits: "Producer: James Rodriguez | Editor: Maya Patel"
    },
    {
      id: 3,
      title: "Artisan Collection",
      category: "commercial",
      type: "Commercial",
      client: "Heritage Crafts Co.",
      description: "A luxury brand campaign showcasing traditional craftsmanship through modern cinematographic techniques.",
      image: portfolioSample3,
      tags: ["Commercial", "Product", "Studio"],
      credits: "Agency: Creative Collective | Brand: Heritage Crafts"
    },
    {
      id: 4,
      title: "Midnight Conversations",
      category: "short-film",
      type: "Short Film",
      client: "Indie Film Festival",
      description: "A narrative short exploring human connection in urban isolation, featuring experimental cinematography.",
      image: portfolioSample1,
      tags: ["Narrative", "Low Light", "Experimental"],
      credits: "Writer/Director: Alex Thompson | Producer: Sam Kim"
    },
    {
      id: 5,
      title: "Future Forward",
      category: "commercial",
      type: "Brand Campaign",
      client: "TechFlow Solutions",
      description: "A multi-platform campaign showcasing innovation through dynamic visuals and seamless storytelling.",
      image: portfolioSample2,
      tags: ["Brand", "Multi-Camera", "Motion Graphics"],
      credits: "Creative Director: Lisa Chen | Agency: Forward Studio"
    },
    {
      id: 6,
      title: "Raw Emotions",
      category: "music-video",
      type: "Music Video",
      client: "Underground Records",
      description: "An emotionally charged music video using natural lighting and handheld cinematography.",
      image: portfolioSample3,
      tags: ["Music Video", "Handheld", "Natural Light"],
      credits: "Director: Marcus Johnson | Artist: River Stone"
    }
  ];

  const categories = [
    { value: "all", label: "All Work" },
    { value: "music-video", label: "Music Videos" },
    { value: "commercial", label: "Commercials" },
    { value: "documentary", label: "Documentaries" },
    { value: "short-film", label: "Short Films" }
  ];

  const filteredItems = selectedCategory === "all" 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  return (
    <section id="portfolio" className="py-24 bg-muted/30">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16 fade-in-up">
          <h2 className="heading-text mb-6 text-foreground">
            Featured Work
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A curated selection of projects that showcase the breadth and depth 
            of my cinematographic vision across various mediums and genres.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category.value}
              variant={selectedCategory === category.value ? "default" : "outline"}
              onClick={() => setSelectedCategory(category.value)}
              className="transition-all duration-300"
            >
              {category.label}
            </Button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item, index) => (
            <Card 
              key={item.id} 
              className="bg-gray-900/50 group overflow-hidden shadow-soft hover:shadow-dramatic transition-all duration-500 fade-in-up hover:border-burnt-orange/50"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="flex gap-3">
                    <Button size="sm" variant="hero" className="shadow-glow">
                      <Play size={16} />
                      Watch
                    </Button>
                    <Button size="sm" variant="cinematic">
                      <ExternalLink size={16} />
                      Details
                    </Button>
                  </div>
                </div>
              </div>
              
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                    {item.title}
                  </h3>
                  <Badge variant="secondary" className="text-xs">
                    {item.type}
                  </Badge>
                </div>
                
                <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                  {item.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {item.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p><strong>Client:</strong> {item.client}</p>
                  <p><strong>Credits:</strong> {item.credits}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FoundersPortfolioSection

